import { FC, useEffect, useState, useMemo, JSX } from "react";
import { Link, useHistory, useRouteMatch } from "react-router-dom";
import { ColumnConfig, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { FontAwesome } from "@app/shared/icons/qesIcons";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { breadcrumbTemplates, BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderLeft from "@app/shared/PageHeaderLeft";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import MemberSetService from "@app/members/memberSetService";
import { MemberSetTypeEnum } from "@app/members/enumerations";
import MemberSetIconBox from "@app/members/MemberSetIconBox";
import { MemberSetModel, Version } from "@app/members/memberSetModels";
import { ResourceType } from "@app/resources";

export interface MemberSetTableData {
  [wafflesKeyColumnName]: string;
  name: JSX.Element;
  description: string;
  lastUpdated: string;
  lastActivation: string;
  status: string;
  type: string;
  isFiltered: boolean;
}

export function memberSetsCustomSort(
  records: MemberSetTableData[],
  column: keyof MemberSetTableData,
  direction: string
): MemberSetTableData[] {
  const data: MemberSetTableData[] = [...records];
  const multiplier: number = direction === SortDirections.Asc ? 1 : -1;

  const filtered: MemberSetTableData[] = data.filter((a: MemberSetTableData) => a.isFiltered);
  const notFiltered: MemberSetTableData[] = data.filter((a: MemberSetTableData) => !a.isFiltered);

  switch (column) {
    case "key":
    case "description":
      return notFiltered
        .sort((a: MemberSetTableData, b: MemberSetTableData) => (a[column] > b[column] ? 1 : -1) * multiplier)
        .concat(
          filtered.sort((a: MemberSetTableData, b: MemberSetTableData) => (a[column] > b[column] ? 1 : -1) * multiplier)
        );

    case "name":
      return notFiltered
        .sort(
          (a: MemberSetTableData, b: MemberSetTableData) =>
            (a[column].key as string).localeCompare(b[column].key as string, "en", { sensitivity: "base" }) *
            multiplier
        )
        .concat(
          filtered.sort(
            (a: MemberSetTableData, b: MemberSetTableData) =>
              (a[column].key as string).localeCompare(b[column].key as string, "en", { sensitivity: "base" }) *
              multiplier
          )
        );

    case "lastUpdated":
      return notFiltered
        .sort(
          (a: MemberSetTableData, b: MemberSetTableData) =>
            (new Date(a.lastUpdated) > new Date(b.lastUpdated) ? 1 : -1) * multiplier
        )
        .concat(
          filtered.sort(
            (a: MemberSetTableData, b: MemberSetTableData) =>
              (new Date(a.lastUpdated) > new Date(b.lastUpdated) ? 1 : -1) * multiplier
          )
        );

    case "lastActivation":
      return notFiltered
        .sort(
          (a: MemberSetTableData, b: MemberSetTableData) =>
            (new Date(a.lastActivation) > new Date(b.lastActivation) ? 1 : -1) * multiplier
        )
        .concat(
          filtered.sort(
            (a: MemberSetTableData, b: MemberSetTableData) =>
              (new Date(a.lastActivation) > new Date(b.lastActivation) ? 1 : -1) * multiplier
          )
        );

    case "status":
    case "type":
      return notFiltered
        .sort((a: MemberSetTableData, b: MemberSetTableData) => (a[column] > b[column] ? 1 : -1) * multiplier)
        .concat(
          filtered.sort((a: MemberSetTableData, b: MemberSetTableData) => (a[column] > b[column] ? 1 : -1) * multiplier)
        );

    default:
      return records;
  }
}

const membersetColumnConfig: ColumnConfig = {
  description: {
    displayText: "Description",
    sortOrder: 20,
    testId: "description",
  },
  lastActivation: {
    displayText: "Last Activation",
    sortOrder: 40,
    testId: "lastActivation",
  },
  lastUpdated: {
    displayText: "Last Updated",
    sortOrder: 30,
    testId: "lastUpdate",
  },
  name: {
    displayText: "Name",
    sortOrder: 10,
    testId: "name",
  },
  status: {
    displayText: "Status",
    sortOrder: 50,
    testId: "status",
  },
  type: {
    displayText: "Type",
    sortOrder: 60,
    testId: "type",
  },
};

export const MemberSets: FC = () => {
  const memberSetService = useMemo(() => new MemberSetService(), []);
  const history = useHistory();
  const routeMatch = useRouteMatch();

  const urlParams: URLSearchParams = new URLSearchParams(window.location.search);

  const [loading, setLoading] = useState<boolean>(false);
  const [hasFetchedMemberSets, setHasFetchedMemberSets] = useState<boolean>(false);

  const [memberSets, setMemberSets] = useState<MemberSetTableData[]>([]);
  const [sharedMemberSets, setSharedMemberSets] = useState<MemberSetTableData[]>([]);
  const [systemMemberSets, setSystemMemberSets] = useState<MemberSetTableData[]>([]);
  const [archivedMemberSets, setArchivedMemberSets] = useState<MemberSetTableData[]>([]);

  const [nameFilter, setNameFilter] = useState<string>(() => urlParams.get("name") ?? "");
  const [descriptionFilter, setDescriptionFilter] = useState<string>(() => urlParams.get("desc") ?? "");
  const [typeFilter, setTypeFilter] = useState<string>(() => urlParams.get("type") ?? "");
  const [statusFilter, setStatusFilter] = useState<string>(() => urlParams.get("status") ?? "");

  const [isFiltered, setIsFiltered] = useState<boolean>(false);
  const [numRecordsDisplayed, setNumRecordsDisplayed] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  useEffect(() => {
    fetchMemberSets();
  }, []);

  const fetchMemberSets = async (forceEmpty = false): Promise<void> => {
    setLoading(true);
    try {
      const response = await memberSetService.getMemberSetsByCriteria(
        true,               // includeShared
        true,               // includeVersions
        false,              // activatedOnly
        true,               // includeArchivedVersions
        false,              // isFilterable
        forceEmpty ? "" : nameFilter,
        forceEmpty ? "" : descriptionFilter,
        forceEmpty ? "" : typeFilter,
        forceEmpty ? "" : statusFilter
      );

      fetchMemberSetsResponse(response);
    } finally {
      setLoading(false);
    }
  };

  const fetchMemberSetsResponse = (response: MemberSetModel[]): void => {
    const sets: MemberSetModel[] = [];
    const sharedSets: MemberSetModel[] = [];
    const systemSets: MemberSetModel[] = [];
    const archivedSets: MemberSetModel[] = [];

    response.forEach((ms: MemberSetModel) => {
      if (!ms.resource.isArchived && !ms.resource.isHidden) {
        if (ms.resource.isSystemResource) {
          systemSets.push(ms);
        } else if (!ms.resource.isShared) {
          sets.push(ms);
        } else {
          sharedSets.push(ms);
        }
      } else {
        archivedSets.push(ms);
      }
    });

    sets.sort((a: MemberSetModel) => (a.sourceMemberSetId ? 1 : -1));
    sharedSets.sort((a: MemberSetModel) => (a.sourceMemberSetId ? 1 : -1));
    archivedSets.sort((a: MemberSetModel) => (a.sourceMemberSetId ? 1 : -1));

    setMemberSets(createWafflesTable(sets));
    setSharedMemberSets(createWafflesTable(sharedSets));
    setSystemMemberSets(createWafflesTable(systemSets));
    setArchivedMemberSets(createWafflesTable(archivedSets));

    setHasFetchedMemberSets(true);

    setTotalRecords(response.length);
    setNumRecordsDisplayed(response.length);

    setIsFiltered(
      !!(nameFilter || descriptionFilter || typeFilter || statusFilter)
    );
  };

  const createWafflesTable = (data: MemberSetModel[]): MemberSetTableData[] => {
    return data.map((rec: MemberSetModel) => {
      const sortedVersions: Version[] = [...rec.memberSetVersions].sort(
        (a: Version, b: Version) =>
          new Date(a.dateCreated) < new Date(b.dateCreated) ? 1 : -1
      );
      const latestVersion: Version = sortedVersions[0];

      const activatedVersions: Version[] = rec.memberSetVersions.filter(
        (v: Version) => v.dateActivated != null
      );
      const lastActivatedVersion: number =
        activatedVersions.length > 0
          ? activatedVersions
            .sort((a: Version, b: Version) =>
              new Date(a.dateActivated!) > new Date(b.dateActivated!) ? 1 : -1
            )
            .pop()!.memberSetVersionId
          : 0;

      let status: string = "";
      if (rec.memberSetVersions.some((x: Version) => x.dateActivated == null)) {
        if (rec.memberSetVersions.some((x: Version) => x.dateActivated != null)) {
          if (
            rec.memberSetVersions.some(
              (x: Version) => x.dateActivated == null && x.memberSetVersionId > lastActivatedVersion
            )
          ) {
            status = "New Version Pending";
          }
        } else {
          status = "Publish Pending";
        }
      } else {
        status = "Published";
      }

      const name: JSX.Element = (
        <div key={rec.resource.name}>
          {MemberSetIconBox(rec, `${routeMatch.url}/${rec.memberSetId}/detail`)}
        </div>
      );

      return {
        description: rec.resource.description,
        isFiltered: !!rec.sourceMemberSetId,
        key: rec.memberSetId.toString(),
        lastActivation: latestVersion.dateActivated
          ? new Date(latestVersion.dateActivated).toLocaleString("en-US")
          : "",
        lastUpdated: new Date(latestVersion.dateLastUpdated).toLocaleString("en-US"),
        name,
        status,
        type: rec.memberSetType === MemberSetTypeEnum.Full ? "Listing" : rec.memberSetType,
      };
    });
  };

  const updateUrlParams = (): void => {
    const searchParams = new URLSearchParams();

    if (nameFilter.trim()) {
      searchParams.set("name", nameFilter.trim());
    }
    if (descriptionFilter.trim()) {
      searchParams.set("desc", descriptionFilter.trim());
    }
    if (typeFilter.trim()) {
      searchParams.set("type", typeFilter.trim());
    }
    if (statusFilter.trim()) {
      searchParams.set("status", statusFilter.trim());
    }

    history.replace({
      pathname: history.location.pathname,
      search: searchParams.toString(),
    });
  };

  const handleSearchKeyUp = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key.toLowerCase() === "enter") {
      handleSearch();
    }
  };

  const handleSearch = (): void => {
    updateUrlParams();
    fetchMemberSets();
  };

  const clearFilter = (): void => {
    setNameFilter("");
    setDescriptionFilter("");
    setTypeFilter("");
    setStatusFilter("");

    history.replace({
      pathname: history.location.pathname,
      search: "",
    });

    fetchMemberSets(true);
  };

  const myMemberSetsBody = useMemo((): JSX.Element => {
    if (memberSets.length > 0 || loading || !hasFetchedMemberSets) {
      return (
        <Waffles<MemberSetTableData>
          columns={membersetColumnConfig}
          customSort={memberSetsCustomSort}
          data={memberSets}
          fullWidth={true}
          loading={loading && !hasFetchedMemberSets}
          testId="my-member-set-waffles-table"
        />
      );
    }
    return <p className="lead">There are no member populations.</p>;
  }, [memberSets, loading, hasFetchedMemberSets]);

  const sharedMemberSetsBody = useMemo((): JSX.Element => {
    if (sharedMemberSets.length > 0 || loading || !hasFetchedMemberSets) {
      return (
        <Waffles<MemberSetTableData>
          columns={membersetColumnConfig}
          customSort={memberSetsCustomSort}
          data={sharedMemberSets}
          fullWidth={true}
          loading={loading && !hasFetchedMemberSets}
          testId="shared-member-set-waffles-table"
        />
      );
    }
    return <p className="lead">There are no shared member populations.</p>;
  }, [sharedMemberSets, loading, hasFetchedMemberSets]);

  const systemMemberSetsBody = useMemo((): JSX.Element => {
    if (systemMemberSets.length > 0 || loading || !hasFetchedMemberSets) {
      return (
        <Waffles<MemberSetTableData>
          columns={membersetColumnConfig}
          customSort={memberSetsCustomSort}
          data={systemMemberSets}
          fullWidth={true}
          loading={loading && !hasFetchedMemberSets}
          testId="system-member-set-waffles-table"
        />
      );
    }
    return <p className="lead">There are no system member populations.</p>;
  }, [systemMemberSets, loading, hasFetchedMemberSets]);

  const archivedMemberSetsBody = useMemo((): JSX.Element => {
    if (archivedMemberSets.length > 0 || loading || !hasFetchedMemberSets) {
      return (
        <Waffles<MemberSetTableData>
          columns={membersetColumnConfig}
          customSort={memberSetsCustomSort}
          data={archivedMemberSets}
          fullWidth={true}
          loading={loading && !hasFetchedMemberSets}
          testId="archived-member-set-waffles-table"
        />
      );
    }
    return <p className="lead">There are no archived member populations.</p>;
  }, [archivedMemberSets, loading, hasFetchedMemberSets]);

  const breadcrumbs = breadcrumbTemplates[BreadcrumbTemplateEnum.MemberPopulations];

  return (
    <Page documentTitle="Member Populations">
      <Breadcrumbs items={breadcrumbs} />

      <PageInner>
        <PageHeader title="Member Populations" iconName={FontAwesome.User}>
          <PageHeaderLeft>
            <div className="ms-2">
              <i className="fa fa-share text-muted" />
              <Link
                to={{
                  pathname: `/resources/share`,
                  state: {
                    breadcrumbItems: breadcrumbs,
                    isSingleResource: false,
                    resourceDescription: "Member Population",
                    resourceTypes: [ResourceType.MemberSet],
                  },
                }}
              >
                Share
              </Link>
            </div>
          </PageHeaderLeft>
        </PageHeader>

        <PageSection>
          <div className="member-sets-filters mb-3 mt-5">
            <QESTextInput
              containerClassName="member-sets-filters__input"
              label="Name"
              testId="member-sets-filter__name"
              value={nameFilter}
              onKeyUp={handleSearchKeyUp}
              onChange={(e) => setNameFilter(e.target.value)}
            />
            <QESTextInput
              containerClassName="member-sets-filters__input"
              label="Description"
              testId="member-sets-filter__description"
              value={descriptionFilter}
              onKeyUp={handleSearchKeyUp}
              onChange={(e) => setDescriptionFilter(e.target.value)}
            />
            <QESTextInput
              containerClassName="member-sets-filters__input"
              label="Status"
              testId="member-sets-filter__status"
              value={statusFilter}
              onKeyUp={handleSearchKeyUp}
              onChange={(e) => setStatusFilter(e.target.value)}
            />
            <QESTextInput
              containerClassName="member-sets-filters__input"
              label="Type"
              testId="member-sets-filter__type"
              value={typeFilter}
              onKeyUp={handleSearchKeyUp}
              onChange={(e) => setTypeFilter(e.target.value)}
            />
         
            <QESButton
              qesType="primary"
              qesTestId="member-sets__search-button"
              onClick={handleSearch}
            >
              Search
            </QESButton>

            <QESButton
              qesType="secondary"
              qesTestId="member-sets__clear-filters"
              onClick={clearFilter}
              disabled={!isFiltered}
            >
              Clear
            </QESButton>
          </div>

          <p>
            Displaying {numRecordsDisplayed} of {totalRecords} member populations{" "}
            {isFiltered ? "(filtered)" : ""}
          </p>

          <div className="row mt-3">
            <div className="col-12">
              {hasFetchedMemberSets ? (
                <AccordionContainer>
                  {memberSets.length > 0 && (
                    <Accordion
                      title="My Member Populations"
                      defaultOpen={true}
                      testId="member-sets__my-member-populations-accordion"
                    >
                      {myMemberSetsBody}
                    </Accordion>
                  )}
                  <Accordion
                    title="Shared Member Populations"
                    defaultOpen={memberSets.length === 0}
                    testId="member-sets__shared-member-populations-accordion"
                  >
                    {sharedMemberSetsBody}
                  </Accordion>
                  <RoleRestricted allowedRoles={[Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN]}>
                    <Accordion
                      title="System Member Populations"
                      testId="member-sets__system-member-populations-accordion"
                    >
                      {systemMemberSetsBody}
                    </Accordion>
                  </RoleRestricted>
                  {archivedMemberSets.length > 0 && (
                    <Accordion
                      title="Archived Member Populations"
                      testId="member-sets__archived-member-populations-accordion"
                    >
                      {archivedMemberSetsBody}
                    </Accordion>
                  )}
                </AccordionContainer>
              ) : (
                <LoadingSpinner />
              )}
            </div>
          </div>
        </PageSection>
      </PageInner>
    </Page>
  );
};